import {
  createContext,
  FC,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import useAsyncEffect from 'use-async-effect';
import { getStorageValue, setStorageValue } from '@helpers/storage';
import { StorageTypes, StorageValues } from '@interfaces/storage';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import EventsService from '@services/EventsService';
import ParticipantsService from '@services/ParticipantsService';

import {
  IMessage,
  MessageType,
  EAudioPlaybackType,
} from '@core/assistant/interfaces';
import {
  assistantSize,
  bedrockResultNotFoundReply,
  useAudioZoneBasedContext,
  debugEventId,
  debugTagName,
} from '@core/assistant/constants';
import { SCREENS, AWAITING_RESPONSE_TYPE } from '@core/assistant/interfaces';
import { celebratoryMomentAudioZone } from '@core/assistant/audioZoneScript';
import {
  defaultBedrockReply,
  teleportRegions,
  keys,
  EKeyName,
} from '@core/assistant/voiceCommand';

// TODO: remove socket.io
import {
  IAssistantContext,
  ProviderProps,
  SocketEventRequestType,
  SocketEventResponseType,
  SocketEventDirection,
} from './interfaces';
import { visualizeAudio } from './AudioVisualizer';
import { socket } from './Socket';
import { useMicRecord } from './Record';
import { shared } from './Shared';
import { processActionsAudioPrompt } from './VoiceInput';
import { log, initDebugSendFn } from './Debug';

import {
  LinearScriptPlayer,
  ScriptNode,
} from './scriptPlayer/linearScriptPlayer';
import { ScriptNodeJSON } from './scriptPlayer/intefraces';
import AssistantScriptsService from '@services/AssistantScriptService';
import { IAssistantScript } from '@interfaces/assistantScript';
import AssistantQueryService from '@services/AssistantQueryService';
import { regionNameToCamelCase, formatSpotRegionName } from './Regions';

export const AssistantContext = createContext<IAssistantContext>(
  {} as IAssistantContext,
);

export const useAssistant = () => useContext(AssistantContext);

let visualizerInstance: any;
// TODO: temp workaround, do a proper fix on SM side to have an option to use Transcibe only
// TODO: get rid of AWAITING_RESPONSE_TYPE.LONG
let activeScreenGlob = SCREENS.DEFAULT;
// TODO: temp workaround
let isMutedGlob = false;
let currentRegion = '';
let awaitingResponseTypeGlob = '';
const completedScripts: { [key: string]: boolean } = {};

const getCurrentRegion = () => {
  let currentArea = currentRegion;

  if (!currentRegion && debugTagName) {
    currentArea = debugTagName;
  } else if (!currentRegion && !debugTagName) {
    currentArea = 'landing';
  }

  return currentArea;
};

const AssistantProviderInner: FC<ProviderProps> = ({
  children,
  currentRegionName,
  userName,
  mytaverseEventRef,
  sendWSRef,
}) => {
  const [open, setOpen] = useState(false);
  const [isAudioPlaybackActive, setIsAudioPlaybackActive] = useState(false);
  const [awaitingResponseType, setAwaitingResponseType] = useState(
    AWAITING_RESPONSE_TYPE.NONE,
  );
  const [activeScreen, setActiveScreen] = useState<SCREENS>(SCREENS.DEFAULT);
  const [navigationHistory, setNavigationHistory] = useState<SCREENS[]>([
    SCREENS.DEFAULT,
  ]);
  //const [audioPlayBackType, setAudioPlayBackType] = useState<EAudioPlaybackType>();

  const [scripts, setScripts] = useState<IAssistantScript[]>([]);

  const [audioPromptHistory, setAudioPromptHistory] = useState<string[]>([]);

  const [isConnected, setIsConnected] = useState(socket.connected);
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMutedLoc] = useState(false);
  const [isDisplayUserFeedback, setIsDisplayUserFeedback] = useState(false);
  const [lastUserMessageData, setLastUserMessageData] = useState({
    eventId: '',
    participantId: '',
    timestamp: 0,
  });

  const [chatHistory, setChatHistory] = useState<IMessage[]>([]);

  const progressState = useRef('{}');
  const saveProgressQueueRef = useRef<Promise<void>>(Promise.resolve());

  // refs come from props — no direct hook calls to useMytaverse/useMytaverseEvent here
  const sendJSONMessageToWebSocketRef = sendWSRef;

  const { stopRecording } = useMicRecord();
  // const { isAudioZonePlayback, setIsAudioZonePlayback } = useAudioZone();
  // const { isGreetingsPlayback, setIsGreetingsPlayback } = useGreetings();

  const navigationHistoryRef = useRef(navigationHistory);
  navigationHistoryRef.current = navigationHistory;

  const openRef = useRef(open);
  openRef.current = open;

  const scriptsRef = useRef(scripts);
  scriptsRef.current = scripts;

  const userNameRef = useRef(userName);
  userNameRef.current = userName;

  const linearScriptPlayerRef = useRef<LinearScriptPlayer>();
  const isAudioPlaybackActiveRef = useRef<boolean>(false);
  const audioPlaybackIdRef = useRef<number>(0);
  const preRenderedAudioRef = useRef<
    {
      text: string;
      audio: null | ArrayBuffer;
      voiceId: string;
      engine: string;
      area: string;
      index: number;
    }[]
  >([]);

  const setIsMuted = useCallback(() => {
    setIsMutedLoc((prev) => {
      isMutedGlob = !prev;
      return !prev;
    });
  }, []);

  const navigate = useCallback((screen: SCREENS, navigateBack?: boolean) => {
    if (navigateBack) {
      const history = navigationHistoryRef.current;
      activeScreenGlob = history[history.length - 2];
      setActiveScreen(history[history.length - 2]);
    } else {
      activeScreenGlob = screen;
      setActiveScreen(screen);
    }

    setNavigationHistory((prevState) => {
      if (navigateBack) {
        prevState.pop();
        return [...prevState];
      }

      return [...prevState, screen];
    });
  }, []);

  const addMessage = useCallback(
    ({ text, type }: { text: string; type: MessageType }) => {
      setChatHistory((prevState) => {
        return [...prevState, { text, type, timestamp: Date.now() }];
      });
    },
    [],
  );

  const addAudioHistory = useCallback((text: string) => {
    setAudioPromptHistory((prevState) => {
      return [...prevState, text];
    });
  }, []);

  const handleSendRequest = useCallback(
    async (text: string) => {
      addMessage({ text, type: MessageType.USER });
      setIsDisplayUserFeedback(false);
      setAwaitingResponseType(AWAITING_RESPONSE_TYPE.SHORT);

      shared.audioPlayBackType = EAudioPlaybackType.CHAT;
      log('currentRegion', currentRegion);

      if (useAudioZoneBasedContext) {
        const currentArea = getCurrentRegion();

        log('useAudioZoneBasedContext currentRegion', currentArea);
        log(mytaverseEventRef.current?.currentEventId);

        const modelResponse = await AssistantQueryService.queryAssistant({
          text,
          tag: formatSpotRegionName(currentArea),
          eventId: mytaverseEventRef.current?.currentEventId || 'debug',
          includeScriptContext:
            mytaverseEventRef.current?.currentEventId || debugTagName
              ? true
              : false,
        });

        setAwaitingResponseType(AWAITING_RESPONSE_TYPE.NONE);
        awaitingResponseTypeGlob = AWAITING_RESPONSE_TYPE.NONE;

        if (modelResponse.text !== "I don't know.") {
          generatePollyAudio(modelResponse.text);
        }

        addMessage({ text: modelResponse.text, type: MessageType.ASSISTANT });

        if (
          modelResponse.text === "I don't know." &&
          modelResponse.lastUserMessageData
        ) {
          setIsDisplayUserFeedback(true);
          setLastUserMessageData(modelResponse.lastUserMessageData);
        }

        log('useAudioZoneBasedContext modelResponse', modelResponse);
      } else {
        socket.emit(SocketEventDirection.request, {
          type: SocketEventRequestType.bedrock,
          data: { text },
        });
      }
    },
    [addMessage],
  );

  const handleSubmitUserFeedback = useCallback(
    async (isUserApproved: boolean) => {
      await AssistantQueryService.flagAssistantReply({
        eventId: lastUserMessageData.eventId,
        timestamp: lastUserMessageData.timestamp,
        isFeedbackRequired: isUserApproved ? 2 : 3,
      });
      setIsDisplayUserFeedback(false);

      if (isUserApproved) {
        addMessage({
          text: 'Your request is recorded',
          type: MessageType.ASSISTANT,
        });
      }
    },
    [lastUserMessageData, addMessage],
  );

  const isAIEnabledForEvent = useCallback(() => {
    const ep = mytaverseEventRef.current;
    const settings = ep.events?.find(
      (x: any) => x.eventId === ep.currentEventId,
    )?.settings;

    if (
      ep?.currentEventId &&
      settings?.aiEnabled &&
      settings?.aiVirtualAssistantName === 'Noa'
    ) {
      return true;
    }

    return false;
  }, []);

  function audioEndCallback(playbackId: number) {
    log('[audio] audioEndCallback fired', {
      playbackId,
      current: audioPlaybackIdRef.current,
    });
    if (playbackId !== audioPlaybackIdRef.current) {
      log(
        '[audio] audioEndCallback STALE — ignoring (stopped manually before natural end)',
      );
      return;
    }
    log('[audio] audioEndCallback NATURAL END');
    isAudioPlaybackActiveRef.current = false;
    linearScriptPlayerRef.current?.notifyAudioEnded();

    const linearScriptPlayerState =
      linearScriptPlayerRef.current?.getCurrentState();

    if (
      linearScriptPlayerState?.isRunning &&
      !linearScriptPlayerState?.currentNode.advanceOnAudioEnd
    ) {
      setIsAudioPlaybackActive(false);
    }

    console.log('audioEndCallback', {
      audioPlayBackType: shared.audioPlayBackType,
      currentRegion,
      linearScriptPlayerState,
    });

    if (
      linearScriptPlayerState?.isRunning &&
      linearScriptPlayerState?.currentNode.advanceOnAudioEnd
    ) {
      log('auto advance script');
      linearScriptPlayerRef.current?.triggerNext();
      log(
        'script current state',
        linearScriptPlayerRef.current?.getCurrentState(),
      );

      /*if (
        !linearScriptPlayerRef.current?.getCurrentState().isRunning &&
        linearScriptPlayerRef.current?.getCurrentState().name
      ) {
        completedScripts[
          linearScriptPlayerRef.current?.getCurrentState().name
        ] = true;
      }*/

      log('completedScripts', completedScripts);
    }

    log(
      'current node',
      linearScriptPlayerRef.current?.getCurrentState().currentNode.id,
    );

    if (currentRegion === celebratoryMomentAudioZone) {
      log('trigger celebratory moment websocket message');

      const wsMessage = {
        action: WebsocketAction.MytaverseAction,
        tag: 'MytaEvent.Action.CelebrationMoment.Start',
        data: '',
      };
      log('sendJSONMessageToWebSocket', wsMessage);

      sendJSONMessageToWebSocketRef.current(wsMessage);
    }

    if (shared.audioPlayBackType === EAudioPlaybackType.AUDIO_ZONE) {
      //setOpen(false);
    }

    if (shared.audioPlayBackType === EAudioPlaybackType.GREETINGS) {
      const assistantStorage = getStorageValue<any>(
        StorageValues.Assistant,
      ) || {
        isFirstTime: true,
      };

      if (!assistantStorage.isFirstTime) {
        //setOpen(false);
      }
    }

    //setIsGreetingsPlayback(false);
    //setIsAudioZonePlayback(false);
  }

  const stopPlayback = useCallback(() => {
    log('[audio] stopPlayback', {
      nextPlaybackId: audioPlaybackIdRef.current + 1,
    });
    console.trace('[assistant] stopPlayback caller');
    audioPlaybackIdRef.current++;
    isAudioPlaybackActiveRef.current = false;
    setIsAudioPlaybackActive(false);

    try {
      visualizerInstance.stopAudio();
    } catch (e) {}
  }, []);

  const teleportToRegion = useCallback((regionName: string) => {
    log('teleport region', regionName);

    const wsMessage = {
      action: WebsocketAction.MytaverseAction,
      tag: 'MytaEvent.Action.Teleport.ToRegion',
      data: regionName,
    };
    log('sendJSONMessageToWebSocket', wsMessage);

    sendJSONMessageToWebSocketRef.current(wsMessage);
  }, []);

  function onConnect() {
    log('connected to AI API');
    setIsConnected(true);
  }

  function onDisconnect() {
    log('disconnected from AI API');
    setIsConnected(false);
  }

  async function onQueryBedrock(data: any) {
    if (
      activeScreenGlob !== SCREENS.DEFAULT &&
      activeScreenGlob !== SCREENS.TELEPORT
    ) {
      // if custom model cannot find answer retry query using general purpose model
      if (
        awaitingResponseTypeGlob !== AWAITING_RESPONSE_TYPE.LONG &&
        data.includes(bedrockResultNotFoundReply)
      ) {
        setAwaitingResponseType(AWAITING_RESPONSE_TYPE.LONG);
        awaitingResponseTypeGlob = AWAITING_RESPONSE_TYPE.LONG;
      } else {
        setAwaitingResponseType(AWAITING_RESPONSE_TYPE.NONE);
        awaitingResponseTypeGlob = AWAITING_RESPONSE_TYPE.NONE;
        generatePollyAudio(data);
        addMessage({ text: data, type: MessageType.ASSISTANT });
      }
    }
  }

  function onGenerateAudio(data: any) {
    log('activeScreen', activeScreenGlob);
    log('isMutedGlob', isMutedGlob);

    if (/*activeScreenGlob !== SCREENS.DEFAULT &&*/ !isMutedGlob) {
      log('onGenerateAudio data len', data.length);
      isAudioPlaybackActiveRef.current = true;
      log('isAudioPlaybackActiveRef', isAudioPlaybackActiveRef);
      setIsAudioPlaybackActive(true);
      const capturedPlaybackId = audioPlaybackIdRef.current;

      // important, check if audio is playing before stopping to avoid audio glitches
      if (visualizerInstance && visualizerInstance.playing) {
        log(
          '[audio] onGenerateAudio stopping previous visualizer before starting new one',
        );
        visualizerInstance.stopAudio();
      }
      log('[audio] onGenerateAudio START', { playbackId: capturedPlaybackId });
      visualizerInstance = visualizeAudio(
        assistantSize.width,
        assistantSize.height,
        () => audioEndCallback(capturedPlaybackId),
      );
      // TODO: figure out why after init there is a small audio glitch when payback starts
      visualizerInstance.initialize(data);
    }
  }

  function onTranscription(data: any) {
    log('onTranscription', data);

    if (data.isFinal) {
      stopRecording();
      setIsRecording(false);

      if (activeScreenGlob === SCREENS.DEFAULT) {
        const actionsTranscribePromptResult = processActionsAudioPrompt(
          data.text.toLowerCase(),
        );

        log('actions prompt', {
          prompt: data.text,
          result: actionsTranscribePromptResult,
        });

        if (actionsTranscribePromptResult) {
          shared.audioPlayBackType = EAudioPlaybackType.VOICE_COMMAND;

          if (actionsTranscribePromptResult.indexOf('expression') > -1) {
            const enterEvent = new KeyboardEvent(
              'keydown',
              keys[actionsTranscribePromptResult as EKeyName],
            );
            // TODO
            /*mytaverseEventRef.current.gameCastRef.current.detachInput();
            mytaverseEventRef.current.gameCastRef.current.attachInput();
            mytaverseEventRef.current.gameCastRef.current.processKeyboardEvent(
              enterEvent,
            );*/
          } else if (actionsTranscribePromptResult.indexOf('teleport') > -1) {
            for (let i = 0; i < teleportRegions.length; i++) {
              if (
                actionsTranscribePromptResult.indexOf(teleportRegions[i].name) >
                -1
              ) {
                teleportToRegion(teleportRegions[i].ueName as string);
                break;
              }
            }
          }

          addAudioHistory(data.text);
        } else {
          // playback default audio reply
          generatePollyAudio(defaultBedrockReply);
          navigate(SCREENS.TELEPORT);
        }
      } else {
        addMessage({ text: data.text, type: MessageType.USER });
      }
    }
  }

  const generatePollyAudio = async (
    text: string,
    engine?: string,
    voiceId?: string,
    disableOptimizations: boolean = true,
  ) => {
    log('generatePollyAudio muted', isMutedGlob);
    if (isMutedGlob) {
      return;
    }

    if (isAudioPlaybackActiveRef.current && visualizerInstance) {
      log('[audio] generatePollyAudio stopping previous audio for new request');
      visualizerInstance.stopAudio();
    }
    isAudioPlaybackActiveRef.current = false;

    const existingAudio = preRenderedAudioRef.current.find(
      (x) => x.text === text,
    );

    log('existingAudio', existingAudio);

    if (existingAudio?.audio) {
      log('prerendered audio exists', existingAudio);
      log(
        `Polly audio size before playback: ${(
          existingAudio.audio.byteLength / 1024
        ).toFixed(2)} KB`,
      );
      onGenerateAudio(existingAudio.audio);
      return;
    }

    let arrayBuffer: ArrayBuffer | null = null;

    try {
      arrayBuffer = await AssistantQueryService.generateAssistantAudio({
        text,
        disableOptimizations,
        engine,
        voiceId,
      });
    } catch (e) {
      log('Error: failed to fetch Polly audio', e);
      return;
    }

    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      log('Error: empty or missing Polly audio response');
      return;
    }

    log(
      `Polly audio size before playback: ${(
        arrayBuffer.byteLength / 1024
      ).toFixed(2)} KB`,
    );

    if (existingAudio) {
      existingAudio.audio = arrayBuffer;
    }

    onGenerateAudio(arrayBuffer);
  };

  const playBlockAudio = useCallback(
    async (text: string, engine?: string, voiceId?: string) => {
      log('playback block text ->', text);

      if (!openRef.current) {
        setOpen(true);

        setTimeout(() => {
          generatePollyAudio(text, engine, voiceId, false);
        }, 100);
        return;
      }
      generatePollyAudio(text, engine, voiceId, false);
    },
    [],
  );

  // Serialize all server saves through a queue so concurrent calls can't
  // arrive out of order and overwrite each other. Each enqueued save always
  // reads progressState.current at execution time, so it always sends the
  // latest accumulated state.
  const enqueueProgressSave = useCallback(() => {
    const stateAtEnqueue = progressState.current;
    console.log(
      '[progress] enqueueProgressSave called, state at enqueue:',
      stateAtEnqueue,
    );
    saveProgressQueueRef.current = saveProgressQueueRef.current.then(
      async () => {
        console.log(
          '[progress] queue item executing, state at execute time:',
          progressState.current,
        );
        if (mytaverseEventRef.current?.currentEventId || debugEventId) {
          await EventsService.setEventUserData(
            mytaverseEventRef.current?.currentEventId ?? debugEventId,
            { assistantState: progressState.current },
          );
          console.log(
            '[progress] server save done, saved:',
            progressState.current,
          );
        } else {
          console.log('[progress] SKIPPED server save — no eventId');
        }
      },
    );
  }, []);

  const cbOnCompletedStep = useCallback(
    (trigger: string, stepNumber: number, step: string) => {
      console.log(
        `[progress] cbOnCompletedStep called — trigger=${trigger} stepNumber=${stepNumber} step=${step} state BEFORE:`,
        progressState.current,
      );
      log(`${trigger} step ${step} completed`, {
        progressState: progressState.current,
        trigger,
      });

      const currentProgressState: {
        [key: string]: Array<[number, number, number[]?]>;
      } = JSON.parse(progressState.current || '{}');

      if (!currentProgressState[trigger]) {
        currentProgressState[trigger] = [];
      }
      // skip saving progress if entry already exists to avoid duplicates
      const progressEntryAlreadyExists = currentProgressState[trigger].some(
        (e) => e[0] === stepNumber,
      );
      if (!progressEntryAlreadyExists) {
        currentProgressState[trigger].push([stepNumber, Date.now()]);
      }

      progressState.current = JSON.stringify(currentProgressState);
      console.log(
        `[progress] cbOnCompletedStep state AFTER write:`,
        progressState.current,
      );

      log(
        'assistant progress saved',
        JSON.stringify(currentProgressState, null, 2),
      );

      enqueueProgressSave();
    },
    [enqueueProgressSave],
  );

  const saveQuizAnswers = useCallback(
    async (selectedOptions: number[]) => {
      const player = linearScriptPlayerRef.current;
      const trigger = player?.getCurrentState().name;
      const nodeId = player?.getCurrentNodeId();

      log('saveQuizAnswers input', {
        trigger,
        nodeId,
        progressState: progressState.current,
      });

      if (!trigger || !nodeId) {
        log('saveQuizAnswers: no trigger or nodeId');
        return;
      }

      const stepNumber = parseInt(nodeId.replace('step-', ''), 10) - 1;
      if (isNaN(stepNumber)) {
        log('saveQuizAnswers: invalid stepNumber from nodeId', nodeId);
        return;
      }

      const currentProgressState: {
        [key: string]: Array<[number, number, number[]?]>;
      } = JSON.parse(progressState.current || '{}');

      if (!currentProgressState[trigger]) {
        currentProgressState[trigger] = [];
      }
      const entries = currentProgressState[trigger];

      const entryIndex = entries.findIndex((e) => e[0] === stepNumber);

      if (entryIndex !== -1) {
        // cbOnCompletedStep ran first and entry already exists, add collected answers
        entries[entryIndex][2] = selectedOptions;
      } else {
        // cbOnCompletedStep hasn't run yet, create entry now with collected answers
        entries.push([stepNumber, Date.now(), selectedOptions]);
      }
      progressState.current = JSON.stringify(currentProgressState);

      log('quiz answers saved', JSON.stringify(currentProgressState, null, 2));

      enqueueProgressSave();
    },
    [enqueueProgressSave],
  );

  const startFlow = useCallback((scriptTrigger: string, scriptId?: string) => {
    const currentScripts = scriptsRef.current;
    const script = scriptId
      ? currentScripts.find((x) => x.scriptId === scriptId)
      : currentScripts.find(
          (x) =>
            x.scriptTrigger === scriptTrigger ||
            x.scriptTrigger.includes(scriptTrigger),
        );
    const scriptData = script?.scriptData;

    let parsedScriptData: ScriptNodeJSON[] = [];

    if (scriptData) {
      try {
        parsedScriptData = JSON.parse(scriptData);
        log('whole script', JSON.stringify(parsedScriptData, null, 2));
      } catch (error) {
        log('makePlayerFromScriptData error', error);
      }
    }

    const currentArea =
      scriptTrigger && !scriptId ? scriptTrigger : getCurrentRegion();

    const playerState = linearScriptPlayerRef.current?.getCurrentState();
    if (playerState?.isRunning && playerState?.name === currentArea) {
      log('script player is already running');
      return;
    }

    const currentProgressState = JSON.parse(progressState.current || '{}');

    log('currentProgressState player', {
      currentProgressState,
      currentAreaProgress:
        currentProgressState?.[currentArea]?.at(-1)?.[0] ?? 0,
      currentAreaTotalSteps: parsedScriptData.length,
    });

    if (
      parsedScriptData.length > 0 &&
      currentProgressState?.[currentArea] &&
      (currentProgressState?.[currentArea]?.at(-1)?.[0] ?? 0) >=
        parsedScriptData.length - 1
    ) {
      log('script is already completed');
      return;
    }

    const nodes: ScriptNode[] = parsedScriptData.map((node, index) => {
      let advanceOnAudioEndFallback = false;
      // inject user name
      node.text = node.text.replace(
        '{{UserName}}',
        userNameRef.current ? ', ' + userNameRef.current : '',
      );

      // inject experience name
      node.text = node.text.replace(
        '{{ExperienceName}}',
        mytaverseEventRef.current.currentEvent?.name
          ? ', ' + mytaverseEventRef.current.currentEvent?.name
          : '',
      );

      // force script completion on last step if no advance trigger is specified
      if (
        index === parsedScriptData.length - 1 &&
        !node.awaitButtonNext &&
        !node.questionary &&
        !node.advanceOnAudioEnd &&
        !node.allowedKeys?.length &&
        !node.enableClickAnywhere
      ) {
        advanceOnAudioEndFallback = true;
      }

      if (!preRenderedAudioRef.current.find((x) => x.text === node.text)) {
        preRenderedAudioRef.current.push({
          area: currentArea,
          index: index + 1,
          audio: null,
          text: node.text,
          engine: script?.engine as string,
          voiceId: script?.voiceId as string,
        });
      }

      return {
        id: `step-${index + 1}`,
        play: () => {
          cbOnCompletedStep(currentArea, index, `step-${index + 1}`);
          return playBlockAudio(node.text, script?.engine, script?.voiceId);
        },
        awaitButtonNext: node.questionary
          ? true
          : node.awaitButtonNext ?? false,
        awaitAudioEndBeforeInput: node.allowedKeys?.length ? true : false,
        advanceOnAudioEnd: node.advanceOnAudioEnd ?? advanceOnAudioEndFallback,
        delayBeforeAdvancingMs: node.delayBeforeAdvancingMs ?? 0,
        enableClickAnywhere: node.enableClickAnywhere ?? false,
        allowedKeys: node.allowedKeys || [],
        cbOnCompletedStep: () => {},
        questionary: node.questionary,
        text: node.text,
        //cbOnCompletedStep(currentArea, index + 1, `step-${index + 1}`),
      };
    });

    nodes.push({
      id: `step-${nodes.length + 1}`,
      play: async () => {
        linearScriptPlayerRef.current?.complete();

        /*cbOnCompletedStep(
          currentArea,
          parsedScriptData.length,
          `step-${parsedScriptData.length}`,
        );*/

        log('completedScripts', completedScripts);
      },
    });

    log('build nodes', nodes);

    log('preRenderedAudio ' + currentArea, preRenderedAudioRef.current);

    if (
      !currentProgressState[currentArea] ||
      (currentProgressState?.[currentArea] &&
        (currentProgressState?.[currentArea]?.at(-1)?.[0] ?? 0) <
          nodes.length - 2)
    ) {
      linearScriptPlayerRef.current = new LinearScriptPlayer(nodes, {
        name: currentArea,
      });
      let step = 1;

      if (currentProgressState?.[currentArea]) {
        step = (currentProgressState?.[currentArea]?.at(-1)?.[0] ?? -1) + 2;
      }

      console.log(
        `[progress] startFlow starting player at step-${step}, progressState:`,
        progressState.current,
      );
      linearScriptPlayerRef.current.start(`step-${step}`);

      startAudioPrerender(currentArea, step);

      //log('polly response', pollyResponse);
    }
  }, []);

  const startAudioPrerender = async (
    currentArea: string,
    startStep: number,
  ) => {
    const preRenderedAudioScript = preRenderedAudioRef.current.filter(
      (x) => x.area === currentArea && !x.audio,
    );
    log(
      'startAudioPrerender audio ' + currentArea,
      preRenderedAudioScript.length,
    );

    for (let i = startStep; i < preRenderedAudioScript.length; i++) {
      log('startAudioPrerender audio ' + i);

      try {
        const arrayBuffer = await AssistantQueryService.generateAssistantAudio({
          text: preRenderedAudioScript[i].text,
          disableOptimizations: true,
          engine: preRenderedAudioScript[i].engine,
          voiceId: preRenderedAudioScript[i].voiceId,
        });

        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          log('Error: empty or missing Polly audio response for step ' + i);
          continue;
        }

        preRenderedAudioScript[i].audio = arrayBuffer;
        log(
          'startAudioPrerender audio ' + i,
          `${(arrayBuffer.byteLength / 1024).toFixed(2)} KB`,
        );
      } catch (e) {
        log('Error: failed to fetch Polly audio for step ' + i, e);
      }
    }

    log('preRenderedAudioScript', preRenderedAudioScript);
  };

  const fetchScripts = useCallback(async () => {
    const scripts = await AssistantScriptsService.getAssistantScripts();

    log('loaded assistant scripts', scripts);

    setScripts(scripts);
  }, []);

  const mergeProgressStates = (
    current: string | null | undefined,
    stored: string | null | undefined,
  ): string => {
    const currentState: Record<
      string,
      Array<[number, number, number[]?]>
    > = JSON.parse(current || '{}');
    const storedState: Record<
      string,
      Array<[number, number, number[]?]>
    > = JSON.parse(stored || '{}');

    const merged = { ...storedState };
    for (const [key, entries] of Object.entries(currentState)) {
      if (!merged[key]) {
        merged[key] = entries;
      } else {
        // keep all entries from current that db doesn't have
        for (const entry of entries) {
          const exists = merged[key].some((e) => e[0] === entry[0]);
          if (!exists) {
            merged[key].push(entry);
          }
        }
        // sort by stepNumber ascending
        merged[key].sort((a, b) => a[0] - b[0]);
      }
    }
    return JSON.stringify(merged);
  };

  const fetchParticipantProgress = useCallback(async () => {
    console.log(
      '[progress] fetchParticipantProgress called, current state BEFORE:',
      progressState.current,
    );
    if (!mytaverseEventRef.current?.currentEventId) {
      // debug only
      const allParticipantEvents =
        await ParticipantsService.getCurrentParticipantEvents();
      const eventParticipantData = allParticipantEvents.events.find(
        (event) => event.eventId === debugEventId,
      );

      if (eventParticipantData) {
        console.log(
          '[progress] fetchParticipantProgress server assistantState:',
          eventParticipantData?.assistantState,
        );
        progressState.current = mergeProgressStates(
          progressState.current,
          eventParticipantData?.assistantState as string,
        );
      } else {
        log('no debug state data');
      }
    } else {
      console.log(
        '[progress] fetchParticipantProgress server assistantState:',
        mytaverseEventRef.current.currentEventParticipant?.assistantState,
      );
      progressState.current = mergeProgressStates(
        progressState.current,
        mytaverseEventRef.current.currentEventParticipant
          ?.assistantState as string,
      );
    }

    console.log(
      '[progress] fetchParticipantProgress state AFTER merge:',
      progressState.current,
    );
    log('loaded participant progress', progressState.current);
  }, []);

  const resetProgress = useCallback(async () => {
    console.log('[progress] resetProgress called');
    // Clear in-memory state immediately so any writes that happen while the
    // HTTP request is in-flight start from a clean slate.
    progressState.current = '{}';
    // Make the reset save the new queue base so that any saves enqueued
    // after this are guaranteed to arrive at the server AFTER the reset.
    const resetSave = EventsService.setEventUserData(
      mytaverseEventRef.current?.currentEventId ?? debugEventId,
      { assistantState: null },
    ).catch(() => {});
    saveProgressQueueRef.current = resetSave;
    await resetSave;
    console.log('[progress] resetProgress done, state:', progressState.current);
  }, []);

  useEffect(() => {
    initDebugSendFn((msg) => sendJSONMessageToWebSocketRef.current(msg));
    return () => initDebugSendFn(() => {});
  }, []);

  useEffect(() => {
    const socketEventHandler = (msg: any) => {
      log('SocketEventResponse', msg);

      switch (msg.type) {
        case SocketEventResponseType.bedrock:
          onQueryBedrock(msg.data.text);
          break;
        case SocketEventResponseType.polly:
          onGenerateAudio(msg.data.audio);
          break;
        case SocketEventResponseType.error:
          log('Server error:', msg.data.message);
          break;
        case SocketEventResponseType.transcription:
          onTranscription(msg.data);
          break;
      }
    };

    socket.on(SocketEventDirection.response, socketEventHandler);

    return () => {
      socket.off(SocketEventDirection.response, socketEventHandler);
    };
  }, []);

  /*useEffect(() => {
    if (isAudioZonePlayback) {
      log('start audio zone playback');
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isAudioZonePlayback]);

  useEffect(() => {
    log('start greetings playback', isGreetingsPlayback);

    if (isGreetingsPlayback) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isGreetingsPlayback]);*/

  if (!mytaverseEventRef.current) {
    mytaverseEventRef.current = {} as any;
  }

  useEffect(() => {
    // Guard: transient null regions (e.g. from camera-sharing WS messages)
    // should not stop playback or reset the script player.
    if (!currentRegionName) return;

    // Use the prop directly — NOT the live ref. The ref can be null or a stale
    // value from a camera-sharing WS message that arrived between the render
    // that updated the prop and the moment this effect actually runs.
    const incomingRegion = currentRegionName;

    log('region has changed to', incomingRegion);

    // If the script player is already running for this exact region AND audio
    // is actively playing, don't stop and restart — that causes double playback
    // on UE5 reconnect. Require isAudioPlaybackActiveRef so a player stuck in
    // isRunning=true with no audio (e.g. after AudioContext closes) gets reset.
    const playerState = linearScriptPlayerRef.current?.getCurrentState();
    if (
      playerState?.isRunning &&
      playerState.name === incomingRegion &&
      isAudioPlaybackActiveRef.current
    ) {
      log('region script already running with active audio — skipping restart');
      return;
    }

    const scriptData = scriptsRef.current.find((x) => {
      const scriptTrigger = (
        Array.isArray(x.scriptTrigger) ? x.scriptTrigger : [x.scriptTrigger]
      ).map((trigger) =>
        trigger.includes('_') ? regionNameToCamelCase(trigger) : trigger,
      );

      if (scriptTrigger.includes(incomingRegion as string)) {
        return true;
      }
    });

    // If the incoming region has a script that is already completed AND we are
    // actively playing audio for a DIFFERENT region, this is likely a spurious
    // region event (e.g. Zoom SDK join fires a WS message with the Zoom room's
    // topic region). Don't stop the other region's audio — just update the
    // region variable silently and let the correction arrive.
    // IMPORTANT: only apply this when protecting a different region's audio.
    // If nothing is playing (or this IS the current region), fall through so
    // stale server-side completion state doesn't permanently block audio here.
    if (scriptData) {
      const parsedNodes: ScriptNodeJSON[] = JSON.parse(
        scriptData.scriptData || '[]',
      );
      const progress = JSON.parse(progressState.current || '{}');
      const isCompleted =
        parsedNodes.length > 0 &&
        (progress[incomingRegion]?.at(-1)?.[0] ?? 0) >= parsedNodes.length - 1;
      if (isCompleted) {
        const currentPlayerState =
          linearScriptPlayerRef.current?.getCurrentState();
        const protectingDifferentRegion =
          (currentPlayerState?.isRunning || isAudioPlaybackActiveRef.current) &&
          currentPlayerState?.name !== incomingRegion;
        if (protectingDifferentRegion) {
          log(
            'region script already completed — updating region silently, not stopping audio',
            incomingRegion,
          );
          currentRegion = incomingRegion;
          return;
        }
        // Not protecting a different region — fall through to normal flow.
      }
    }

    if (linearScriptPlayerRef.current?.getCurrentState().isRunning) {
      linearScriptPlayerRef.current?.stop();
    }

    stopPlayback();

    if (incomingRegion) {
      currentRegion = incomingRegion;
    }

    if (scriptData) {
      startFlow('', scriptData.scriptId);
    }
  }, [currentRegionName]);

  useAsyncEffect(async () => {
    const userChatHistory = chatHistory.filter((x) => x.type === 'USER');

    if (
      userChatHistory.length &&
      awaitingResponseType === AWAITING_RESPONSE_TYPE.LONG
    ) {
      shared.audioPlayBackType = EAudioPlaybackType.CHAT;
      socket.emit(SocketEventDirection.request, {
        type: SocketEventRequestType.bedrock,
        data: {
          text: userChatHistory[userChatHistory.length - 1].text,
          isGenericModel: true,
        },
      });
    }
  }, [awaitingResponseType, chatHistory]);

  useAsyncEffect(async () => {
    await fetchScripts();

    log('completedScripts', completedScripts);

    await fetchParticipantProgress();
  }, []);

  useEffect(() => {
    log('current assistant progress state', progressState);
  }, [progressState]);

  const contextValue = useMemo(
    () => ({
      activeScreen,
      setActiveScreen,
      open,
      setOpen,
      handleSendRequest,
      chatHistory,
      navigate,
      setNavigationHistory,
      awaitingResponseType,
      stopPlayback,
      isAIEnabledForEvent,
      isRecording,
      setIsRecording,
      audioPromptHistory,
      isMuted,
      setIsMuted,
      teleportToRegion,
      startFlow,
      linearScriptPlayerRef,
      isAudioPlaybackActiveRef,
      isAudioPlaybackActive,
      scripts,
      isDisplayUserFeedback,
      setIsDisplayUserFeedback,
      handleSubmitUserFeedback,
      fetchScripts,
      fetchParticipantProgress,
      resetProgress,
      saveQuizAnswers,
      userName: userName || '',
    }),
    [
      activeScreen,
      open,
      handleSendRequest,
      chatHistory,
      navigate,
      awaitingResponseType,
      stopPlayback,
      isAIEnabledForEvent,
      isRecording,
      audioPromptHistory,
      isMuted,
      setIsMuted,
      teleportToRegion,
      startFlow,
      isAudioPlaybackActive,
      scripts,
      isDisplayUserFeedback,
      handleSubmitUserFeedback,
      fetchScripts,
      fetchParticipantProgress,
      resetProgress,
      saveQuizAnswers,
      userName,
    ],
  );

  return (
    <AssistantContext.Provider value={contextValue}>
      {children}
    </AssistantContext.Provider>
  );
};

export const AssistantProvider = memo(AssistantProviderInner);
